import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Eye, EyeOff, Globe, Mail, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LANG_OPTIONS = [
  { code: 'en', label: 'English' },
  // { code: 'ar', label: 'العربية' },
  { code: 'fr', label: 'Français' },
];

const Register = () => {
  const [step, setStep] = useState(1); // 1=form, 2=verify code
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '', password: '', confirmPassword: '', date_of_birth: ''
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { lang, setLanguage, t } = useLanguage();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Step 1: Validate form and send verification code
  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    if (formData.password !== formData.confirmPassword) { setError(t.passwordsMismatch || 'Passwords do not match'); return; }
    if (formData.password.length < 6) { setError(t.passwordTooShort || 'Password must be at least 6 characters'); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-email`, { email: formData.email.trim().toLowerCase() });
      setStep(2);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to send verification code. Please try again.');
    }
    finally { setLoading(false); }
  };

  // Step 2: Submit registration with verification code
  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { confirmPassword, first_name, last_name, ...rest } = formData;
      const registerData = {
        ...rest,
        email: rest.email.trim().toLowerCase(),
        name: `${first_name} ${last_name}`.trim(),
        verification_code: verificationCode,
      };

      const res = await axios.post(`${API}/auth/register`, registerData);
      try { localStorage.setItem('token', res.data.token); } catch {}
      toast.success(t.registerSuccess || 'Registration successful!');
      window.location.href = '/portal/dashboard';
    } catch (err) {
      setError(err?.response?.data?.detail || 'Registration failed. Please try again.');
    }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex" dir={t.dir}>
      <div className="hidden lg:block lg:w-1/2 relative bg-[#2C3E50]">
        <img src="https://customer-assets.emergentagent.com/job_clinic-portal-51/artifacts/syczyims_IMG_7473.jpeg" alt="Younes Dental Clinic Logo" className="absolute inset-0 w-full h-full object-contain p-16" />
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center px-8 py-12 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-[#1F2937] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              {step === 1 ? (t.createAccount || 'Create Account') : 'Verify Your Email'}
            </h1>
            <p className="text-[#6B7280]">
              {step === 1 ? (t.registerSubtitle || 'Join our patient portal') : `We sent a 6-digit code to ${formData.email}`}
            </p>
          </div>

          {/* Step 1: Registration Form */}
          {step === 1 && (
            <form onSubmit={handleSendCode} className="space-y-4" data-testid="register-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#1F2937] mb-1">{t.firstName}</label>
                  <input name="first_name" type="text" value={formData.first_name} onChange={handleChange} required className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C] text-[16px]" placeholder="John" data-testid="register-first-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1F2937] mb-1">{t.lastName}</label>
                  <input name="last_name" type="text" value={formData.last_name} onChange={handleChange} required className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C] text-[16px]" placeholder="Doe" data-testid="register-last-name-input" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1F2937] mb-1">{t.email}</label>
                <input name="email" type="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C] text-[16px]" placeholder={t.emailPlaceholder} data-testid="register-email-input" />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1F2937] mb-1">{t.phone}</label>
                <input name="phone" type="tel" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C] text-[16px]" placeholder="+1 (555) 123-4567" data-testid="register-phone-input" />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1F2937] mb-1">{t.dateOfBirth}</label>
                <input name="date_of_birth" type="date" value={formData.date_of_birth} onChange={handleChange} required className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C] text-[16px]" data-testid="register-dob-input" />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1F2937] mb-1">{t.password}</label>
                <div className="relative">
                  <input name="password" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={handleChange} required className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C] text-[16px]" placeholder="Min 6 characters" data-testid="register-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#1F2937]">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1F2937] mb-1">{t.confirmPassword}</label>
                <input name="confirmPassword" type={showPassword ? 'text' : 'password'} value={formData.confirmPassword} onChange={handleChange} required className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C] text-[16px]" placeholder="Re-enter password" data-testid="register-confirm-password-input" />
              </div>

              {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="register-error">{error}</div>}

              <button type="submit" disabled={loading} className="w-full bg-[#0F4C5C] text-white hover:bg-[#0F4C5C]/90 rounded-full px-6 py-3 font-semibold transition-all shadow-lg disabled:opacity-50 text-[16px]" data-testid="register-submit-button">
                {loading ? 'Sending verification...' : 'Continue'}
              </button>
            </form>
          )}

          {/* Step 2: Verify Email Code */}
          {step === 2 && (
            <form onSubmit={handleRegister} className="space-y-5" data-testid="verify-form">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
                <Mail className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                <p className="text-sm text-emerald-800">We sent a 6-digit verification code to <strong>{formData.email}</strong>. Please check your inbox.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1F2937] mb-2">Verification Code</label>
                <input type="text" value={verificationCode} onChange={e => setVerificationCode(e.target.value)} required maxLength={6} pattern="[0-9]{6}" inputMode="numeric"
                  placeholder="000000"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C] text-[16px] text-center text-2xl tracking-[0.5em] font-mono"
                  data-testid="verify-code-input" />
              </div>

              {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="register-error">{error}</div>}

              <button type="submit" disabled={loading} className="w-full bg-[#0F4C5C] text-white hover:bg-[#0F4C5C]/90 rounded-full px-6 py-3 font-semibold transition-all shadow-lg disabled:opacity-50 text-[16px]" data-testid="verify-submit">
                {loading ? 'Creating account...' : 'Create Account'}
              </button>

              <button type="button" onClick={() => { setStep(1); setError(''); }}
                className="w-full text-[#6B7280] hover:text-[#1F2937] text-sm font-medium py-2 transition-colors">
                Go back and edit details
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-[#6B7280]">
              {t.haveAccount}{' '}
              <Link to="/login" className="text-[#0F4C5C] hover:text-[#0F4C5C]/80 font-semibold" data-testid="login-link">{t.signInLink}</Link>
            </p>
          </div>

          <div className="mt-4 flex items-center justify-center gap-1" data-testid="language-selector">
            <Globe className="w-4 h-4 text-[#6B7280]" />
            {LANG_OPTIONS.map((opt, i) => (
              <React.Fragment key={opt.code}>
                {i > 0 && <span className="text-gray-300">|</span>}
                <button onClick={() => setLanguage(opt.code)} className={`px-2 py-1 text-sm rounded transition-colors ${lang === opt.code ? 'text-[#0F4C5C] font-semibold' : 'text-[#6B7280] hover:text-[#1F2937]'}`} data-testid={`lang-${opt.code}`}>
                  {opt.label}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
